import React from 'react'
import Box from '@mui/material/Box'

import { AssistantUiKey } from '../../../../pure-js/libs/assistantMessages'
import { useTranslation } from '../hooks/useIntractionTranslate'

const BADGE_FONT_SIZE_RATIO = 0.55
// Floored and spaced because the badge cannot rely on weight contrast: it pops beside the sidebar's
// regular-weight label, but the assistant header's title is bold too, where it merged into the word.
const BADGE_MIN_FONT_SIZE_PX = 11
const BADGE_LETTER_SPACING_EM = 0.08
const BADGE_GAP_EM = 0.22
const SUFFIX_GAP_EM = 0.3

/** Which product the wordmark names: the 1:1 coach, or the floating support assistant. */
export enum AriaBrandVariant {
  COACH = 'coach',
  ASSISTANT = 'assistant'
}

export interface AriaBrandProps {
  variant?: AriaBrandVariant
  /** Required wherever an ancestor paints text with `background-clip: text` — see the badge fill below. */
  badgeColor?: string
}

/** The "Ariaᴬᴵ Coach" wordmark. Size and colour are inherited from whatever renders it. */
export const AriaBrand: React.FC<AriaBrandProps> = ({ variant = AriaBrandVariant.COACH, badgeColor }) => {
  const { t } = useTranslation()

  return (
    <Box
      component="span"
      sx={{
        // The badge is raised by flex alignment, and is a plain span rather than a `<sup>`. Both matter:
        // a real superscript is shifted above its container's box by the UA baseline shift (which
        // survives blockification as a flex item), and every place this renders hides overflow — the
        // sidebar label for its ellipsis, the panel title for `noWrap` — so its top was sliced off.
        display: 'inline-flex',
        alignItems: 'flex-start',
        whiteSpace: 'nowrap'
      }}
    >
      {t(AssistantUiKey.NAME)}
      <Box
        component="span"
        sx={{
          fontSize: `max(${BADGE_MIN_FONT_SIZE_PX}px, ${BADGE_FONT_SIZE_RATIO}em)`,
          fontWeight: 700,
          letterSpacing: `${BADGE_LETTER_SPACING_EM}em`,
          lineHeight: 1,
          ml: `${BADGE_GAP_EM}em`,
          // The sidebar's active row paints its label with a gradient plus a transparent fill, which the
          // badge would otherwise inherit and render as a blank gap.
          WebkitTextFillColor: badgeColor ?? 'currentColor'
        }}
      >
        {t(AssistantUiKey.BRAND_AI_BADGE)}
      </Box>
      {variant === AriaBrandVariant.COACH ? (
        <Box component="span" sx={{ ml: `${SUFFIX_GAP_EM}em` }}>
          {t(AssistantUiKey.BRAND_COACH_SUFFIX)}
        </Box>
      ) : null}
    </Box>
  )
}

export default AriaBrand
